import { ReactNode, useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { getLegalStatus } from '@/lib/legalVersions';

// Lazy-load LegalConsentModal to reduce initial bundle size
// This modal is only shown after authentication when legal consent is needed
const LegalConsentModal = lazy(() => 
  import('@/components/legal/LegalConsentModal').then(m => ({ default: m.LegalConsentModal }))
);

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireOrgAdmin?: boolean;
}

/**
 * Check if a pathname is in the account-only allowlist.
 * Prefix match for /profile (supports /profile, /profile/*).
 * Exact match for /no-organization.
 * 
 * Used by Gate 5 to allow evicted users (zero org memberships)
 * access to GDPR self-service routes only.
 * See Invariant #46 (INVARIANTS.md).
 */
function isAccountOnlyRoute(pathname: string): boolean {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  if (normalized === '/no-organization') return true;
  if (normalized === '/account/profile' || normalized.startsWith('/account/profile/')) return true;
  if (normalized === '/profile' || normalized.startsWith('/profile/')) return true;
  return false;
}

/**
 * Component to handle invite-only enforcement failure.
 * Signs out the user and redirects to /auth with error parameter.
 * Uses a ref to ensure signOut is called exactly once.
 */
function InviteOnlyBlock({ signOut }: { signOut: () => Promise<void> }) {
  const signedOutRef = useRef(false);

  useEffect(() => {
    if (!signedOutRef.current) {
      signedOutRef.current = true;
      signOut();
    }
  }, [signOut]);

  // Navigate happens immediately; signOut runs in background
  return <Navigate to="/auth?error=no-invite" replace />;
}

/**
 * Global authentication gate for all protected routes.
 * 
 * Security enforcement order (CRITICAL - do not change):
 * 1. Check if user is authenticated
 * 2. Check if MFA is required (has verified factor but at AAL1) - HARD BLOCK
 * 3. Check role requirements (admin, super_admin, org_admin)
 * 4. Check legal consent requirements (first-run + material reaccept for org_admin)
 * 5. Check invite-only requirements (account link + org membership)
 * 
 * This ensures that MFA bypass is impossible - a user with MFA enabled
 * MUST complete TOTP verification before accessing ANY protected content.
 * 
 * Gate 4 enforces:
 * - First-run acceptance: HARD BLOCK when terms/privacy not yet accepted
 * - Material reaccept for org_admin: HARD BLOCK when version mismatch + material mode
 * - Members with material mismatch are NOT blocked here (soft-blocked at action level)
 * 
 * Gate 5 enforces invite-only: users must have:
 * - profile.account_id set (linked via confirm-invite)
 * - account exists and is not disabled
 * - at least one active org_membership (joined_at not null) OR be super_admin
 * 
 * PERFORMANCE NOTE: This component wraps children with AuthenticatedLayout
 * which provides TooltipProvider. Auth routes intentionally do NOT have
 * TooltipProvider to reduce the initial bundle size.
 */
export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireSuperAdmin = false,
  requireOrgAdmin = false,
}: ProtectedRouteProps) {
  const { 
    user, 
    isAdmin,
    isOrgAdmin, 
    isSuperAdmin, 
    profile, 
    account, 
    orgMemberships, 
    loading, 
    refreshProfile, 
    mfaRequired, 
    currentOrgId,
    signOut,
  } = useAuth();
  const location = useLocation();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentAcceptanceMethod, setConsentAcceptanceMethod] = useState<'first_run' | 'material_reaccept'>('first_run');
  const [consentChecked, setConsentChecked] = useState(false);

  // DEBUG: track mount/unmount lifecycle
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.debug('[ProtectedRoute] MOUNTED', { pathname: location.pathname });
    }
    return () => {
      if (import.meta.env.DEV) {
        console.debug('[ProtectedRoute] UNMOUNTED', { pathname: location.pathname });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user needs to accept legal terms (first-run or material reaccept for org_admin)
  useEffect(() => {
    if (profile && !loading) {
      const legalStatus = getLegalStatus(profile);
      
      if (legalStatus.needsFirstRunAcceptance) {
        setShowConsentModal(true);
        setConsentAcceptanceMethod('first_run');
      } else if (isOrgAdmin && legalStatus.anyMaterialReacceptRequired) {
        setShowConsentModal(true);
        setConsentAcceptanceMethod('material_reaccept');
      } else {
        setShowConsentModal(false);
      }
      setConsentChecked(true);
    }
  }, [profile, loading, isOrgAdmin]);

  const handleConsentAccepted = async () => {
    await refreshProfile();
    setShowConsentModal(false);
  };

  // Loading state - blocks until:
  // 1. Initial session check complete
  // 2. If user exists: profile loaded AND MFA status checked
  // This ensures fail-closed behavior: never render protected content prematurely
  if (loading) {
    if (import.meta.env.DEV) {
      console.debug('[ProtectedRoute] Rendering Loading...', {
        pathname: location.pathname,
        hasUser: !!user,
        profileLoaded: !!profile,
        mfaRequired,
      });
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // GATE 1: Not authenticated at all - redirect to login
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // GATE 2: MFA REQUIRED BUT NOT COMPLETED - HARD BLOCK
  // This is the critical security gate. If user has MFA enabled but is at AAL1,
  // they MUST complete MFA verification before accessing any protected content.
  // No exceptions - legal consent, role checks, etc. all come AFTER this.
  if (mfaRequired) {
    const returnPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?mfa=required&redirect=${returnPath}`} replace />;
  }

  // GATE 3: Role-based access control
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // requireOrgAdmin: must be admin AND have a current_org_id
  if (requireOrgAdmin) {
    if (!isAdmin || !currentOrgId) {
      // Redirect super admins to platform interface
      if (isSuperAdmin) {
        return <Navigate to="/platform" replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  // GATE 4: Legal consent requirement
  // Wait for consent to be evaluated before proceeding
  // This prevents protected children from rendering before consent is checked
  if (!consentChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Now consent has been checked — show modal if needed
  // Covers both first-run and material reaccept for org_admin
  // Only shown AFTER MFA is complete (user is at AAL2 or doesn't have MFA)
  if (showConsentModal && user) {
    return (
      <AuthenticatedLayout>
        <Suspense fallback={<div className="min-h-screen" />}>
          <LegalConsentModal
            open={showConsentModal}
            onAccept={handleConsentAccepted}
            userId={user.id}
            acceptanceMethod={consentAcceptanceMethod}
            orgId={currentOrgId}
          />
        </Suspense>
        {/* Show a backdrop/loading state while modal is open */}
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-muted-foreground">Please accept our terms to continue...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  // GATE 5: INVITE-ONLY ENFORCEMENT
  // This gate runs ONLY after loading is complete (checked above),
  // which means orgMemberships is fully resolved (no transient empty array).
  // See Section 1 of the GDPR Self-Service Access plan for readiness guarantees.
  //
  // Criteria:
  // 1. profile.account_id must be set (linked to an account via confirm-invite)
  // 2. account must exist and not be disabled
  // 3. user must have at least one active org_membership (already filtered to joined_at != null) 
  //    OR be super_admin
  //
  // Super admin exception: Super admins can access /platform routes without org membership
  // but NOT org-scoped routes (handled by requireOrgAdmin which checks currentOrgId)
  const hasValidAccountLink = profile?.account_id != null;
  const accountExists = account != null;
  const accountNotDisabled = !account?.is_anonymized;
  const hasActiveOrgMembership = orgMemberships.length > 0;

  const isInviteOnlyValid =
    hasValidAccountLink &&
    accountExists &&
    accountNotDisabled &&
    (hasActiveOrgMembership || isSuperAdmin);

  // CRITICAL: Gate 5 redirect/render happens BEFORE AuthenticatedLayout mounts.
  // This prevents any layout wrapper from triggering org-scoped queries or side-effects.
  if (!isInviteOnlyValid) {
    // 5a: Evicted user -- valid account but no org membership
    // Allow access to account-only routes for GDPR self-service (Article 17)
    const isEvictedUser =
      hasValidAccountLink &&
      accountExists &&
      accountNotDisabled &&
      !hasActiveOrgMembership &&
      !isSuperAdmin;

    if (isEvictedUser) {
      // Redirect evicted users from /profile (inside MainLayout) to /account/profile
      if (location.pathname === '/profile' || location.pathname.startsWith('/profile/')) {
        return <Navigate to="/account/profile" replace />;
      }
      if (!isAccountOnlyRoute(location.pathname)) {
        return <Navigate to="/no-organization" replace />;
      }
      // Allowed route: wrap in AuthenticatedLayout (TooltipProvider only) and render
      return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
    }

    // 5b: Truly invalid session (no account link, anonymized, etc.) -- sign out
    return <InviteOnlyBlock signOut={signOut} />;
  }

  // Normal path: fully valid user with active org membership
  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}
